import { useState, useEffect } from "react";
import api, { fileUrl } from "@/lib/api";
import { VARIANTS, CONDITIONS, CATEGORIES, mergeCategories } from "@/lib/constants";
import { toast } from "sonner";
import { X, Upload, Loader2, Trash2, Sparkles } from "lucide-react";

const inputCls =
  "w-full bg-ink border border-stroke px-3 py-2 font-mono text-sm text-paper placeholder:text-muted-gray focus:outline-none focus:border-acid transition-colors";
const labelCls = "font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-1 block";

const empty = {
  title: "", category: "concert", band: "", poster_artist: "", variant: "standard",
  edition_size: "", print_number: "", width_in: "", height_in: "",
  condition: "Near Mint", framed: false, adult: false, framer: "",
  purchase_price: "", market_value: "", purchase_date: "", show_date: "",
  venue_name: "", venue_city: "", notes: "", image_paths: [],
};

function Field({ label, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}

export default function PosterFormDialog({ open, onClose, onSaved, editing }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [allCats, setAllCats] = useState(CATEGORIES);
  const [customMode, setCustomMode] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomMode(false);
    api.get("/posters/categories").then((r) => setAllCats(mergeCategories(r.data))).catch(() => setAllCats(CATEGORIES));
  }, [open]);

  useEffect(() => {
    if (open) {
      if (editing) {
        const normalized = { ...empty };
        for (const k of Object.keys(empty)) {
          const v = editing[k];
          if (k === "image_paths") normalized[k] = v || [];
          else if (k === "framed" || k === "adult") normalized[k] = !!v;
          else normalized[k] = v == null ? "" : v;
        }
        setForm(normalized);
      } else {
        setForm(empty);
      }
    }
  }, [open, editing]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAiFill = async (e) => {
    const file = (e.target.files || [])[0];
    if (!file) return;
    setAiLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/posters/ai-fill", fd);
      setForm((f) => {
        const merged = { ...f };
        for (const [k, v] of Object.entries(data)) {
          if (v === null || v === undefined || v === "") continue;
          if (k === "adult") merged.adult = !!v;
          else merged[k] = v;
        }
        return merged;
      });
      // also store the analysed image as a poster photo
      const up = new FormData();
      up.append("file", file);
      const res = await api.post("/upload", up);
      setForm((f) => ({ ...f, image_paths: [...(f.image_paths || []), res.data.path] }));
      toast.success("Auto-filled from photo — review & save");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI analysis failed");
    } finally {
      setAiLoading(false);
      e.target.value = "";
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const paths = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        paths.push(data.path);
      }
      set("image_paths", [...(form.image_paths || []), ...paths]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const num = (v) => (v === "" || v == null ? null : Number(v));

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      category: (form.category || "concert").toString().trim().toLowerCase() || "concert",
      edition_size: num(form.edition_size),
      print_number: num(form.print_number),
      width_in: num(form.width_in),
      height_in: num(form.height_in),
      purchase_price: num(form.purchase_price),
      market_value: num(form.market_value),
      lat: editing && form.venue_city === editing.venue_city ? editing.lat : null,
      lng: editing && form.venue_city === editing.venue_city ? editing.lng : null,
    };
    try {
      if (editing) await api.put(`/posters/${editing.id}`, payload);
      else await api.post("/posters", payload);
      toast.success(editing ? "Poster updated" : "Poster added to vault");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-3xl bg-surface border border-stroke my-8" data-testid="poster-form-dialog">
        <div className="flex items-center justify-between p-5 border-b border-stroke sticky top-0 bg-surface z-10">
          <h2 className="font-display text-3xl text-paper">{editing ? "Edit Poster" : "Add Poster"}</h2>
          <button onClick={onClose} data-testid="poster-form-close" className="text-muted-gray hover:text-magenta">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 flex items-center justify-between gap-3 border border-dashed border-fuchsia-500/40 bg-fuchsia-500/5 p-3">
            <div className="font-mono text-[11px] text-fuchsia-200">
              ✨ Snap a poster photo and let AI auto-fill the details
            </div>
            <label data-testid="ai-fill-button" className="cursor-pointer shrink-0 flex items-center gap-2 bg-fuchsia-500 text-white font-mono text-[11px] uppercase tracking-wider font-bold px-4 py-2 hover:bg-fuchsia-400">
              {aiLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              {aiLoading ? "Analyzing..." : "Auto-fill"}
              <input type="file" accept="image/*" className="hidden" onChange={handleAiFill} disabled={aiLoading} />
            </label>
          </div>
          <div className="md:col-span-2">
            <Field label="Poster Title *">
              <input data-testid="poster-title-input" className={inputCls} value={form.title}
                onChange={(e) => set("title", e.target.value)} placeholder="e.g. Foo Fighters - Chicago '24" />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Category">
              <select data-testid="poster-category-select" className={inputCls}
                value={customMode ? "__custom__" : form.category}
                onChange={(e) => {
                  if (e.target.value === "__custom__") { setCustomMode(true); set("category", ""); }
                  else { setCustomMode(false); set("category", e.target.value); }
                }}>
                {allCats.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                <option value="__custom__">+ Add custom category…</option>
              </select>
              {customMode && (
                <input data-testid="poster-custom-category-input" className={`${inputCls} mt-2`}
                  placeholder="Your category name (e.g. Skateboard, Sci-Fi)"
                  value={form.category} onChange={(e) => set("category", e.target.value)} />
              )}
            </Field>
          </div>
          <Field label="Act / Studio / Brand">
            <input data-testid="poster-band-input" className={inputCls} value={form.band}
              onChange={(e) => set("band", e.target.value)} placeholder="Foo Fighters / A24 / Coca-Cola" />
          </Field>
          <Field label="Poster Artist / Designer">
            <input data-testid="poster-artist-input" className={inputCls} value={form.poster_artist}
              onChange={(e) => set("poster_artist", e.target.value)} placeholder="Drew Millward" />
          </Field>

          <Field label="Variant">
            <select data-testid="poster-variant-select" className={inputCls} value={form.variant}
              onChange={(e) => set("variant", e.target.value)}>
              {VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Condition">
            <select data-testid="poster-condition-select" className={inputCls} value={form.condition}
              onChange={(e) => set("condition", e.target.value)}>
              {CONDITIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Print Number">
            <input data-testid="poster-print-number-input" type="number" className={inputCls} value={form.print_number}
              onChange={(e) => set("print_number", e.target.value)} placeholder="45" />
          </Field>
          <Field label="Edition Size">
            <input data-testid="poster-edition-size-input" type="number" className={inputCls} value={form.edition_size}
              onChange={(e) => set("edition_size", e.target.value)} placeholder="100" />
          </Field>

          <Field label='Width (in)'>
            <input type="number" className={inputCls} value={form.width_in}
              onChange={(e) => set("width_in", e.target.value)} placeholder="18" />
          </Field>
          <Field label='Height (in)'>
            <input type="number" className={inputCls} value={form.height_in}
              onChange={(e) => set("height_in", e.target.value)} placeholder="24" />
          </Field>

          <Field label="Purchase Price ($)">
            <input type="number" className={inputCls} value={form.purchase_price}
              onChange={(e) => set("purchase_price", e.target.value)} placeholder="60" />
          </Field>
          <Field label="Market Value ($)">
            <input type="number" className={inputCls} value={form.market_value}
              onChange={(e) => set("market_value", e.target.value)} placeholder="220" />
          </Field>

          <Field label="Show / Release Date">
            <input data-testid="poster-show-date-input" type="date" className={inputCls} value={form.show_date}
              onChange={(e) => set("show_date", e.target.value)} />
          </Field>
          <Field label="Purchase Date">
            <input type="date" className={inputCls} value={form.purchase_date}
              onChange={(e) => set("purchase_date", e.target.value)} />
          </Field>

          <Field label="Venue / Location">
            <input data-testid="poster-venue-input" className={inputCls} value={form.venue_name}
              onChange={(e) => set("venue_name", e.target.value)} placeholder="Metro / Cinema / Broadway" />
          </Field>
          <Field label="City (mapped)">
            <input data-testid="poster-city-input" className={inputCls} value={form.venue_city}
              onChange={(e) => set("venue_city", e.target.value)} placeholder="Chicago, IL" />
          </Field>

          <div className="md:col-span-2 flex items-center gap-3">
            <input id="framed" type="checkbox" checked={form.framed} data-testid="poster-framed-checkbox"
              onChange={(e) => set("framed", e.target.checked)}
              className="w-4 h-4 accent-acid" />
            <label htmlFor="framed" className="font-mono text-xs text-paper">Framed?</label>
            {form.framed && (
              <input className={`${inputCls} flex-1`} value={form.framer}
                onChange={(e) => set("framer", e.target.value)} placeholder="Framer details" />
            )}
          </div>

          <div className="md:col-span-2 flex items-center gap-3">
            <input id="adult" type="checkbox" checked={form.adult} data-testid="poster-adult-checkbox"
              onChange={(e) => set("adult", e.target.checked)}
              className="w-4 h-4 accent-red-500" />
            <label htmlFor="adult" className="font-mono text-xs text-red-300">Adult / 18+ content (age-gated)</label>
          </div>

          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea className={`${inputCls} min-h-[70px] resize-y`} value={form.notes}
                onChange={(e) => set("notes", e.target.value)} placeholder="Signed, misprint, story..." />
            </Field>
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>Poster Photos</label>
            <div className="flex items-center gap-3 flex-wrap">
              {(form.image_paths || []).map((p) => (
                <div key={p} className="relative w-16 h-20 border border-stroke">
                  <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => set("image_paths", form.image_paths.filter((x) => x !== p))}
                    className="absolute -top-2 -right-2 bg-magenta text-white p-0.5">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              <label data-testid="poster-image-upload" className="cursor-pointer flex items-center gap-2 border border-dashed border-stroke px-4 py-3 font-mono text-xs text-muted-gray hover:border-acid hover:text-acid">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                Upload
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
              </label>
            </div>
          </div>
        </div>

        <div className="p-5 border-t border-stroke flex justify-end gap-3 sticky bottom-0 bg-surface">
          <button onClick={onClose} className="px-5 py-2.5 font-mono text-xs uppercase tracking-wider border border-stroke text-muted-gray hover:text-paper">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} data-testid="poster-save-button"
            className="px-6 py-2.5 font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold hover:bg-glow disabled:opacity-50 flex items-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {editing ? "Save Changes" : "Add to Vault"}
          </button>
        </div>
      </div>
    </div>
  );
}
